import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { Box } from '@mui/system';
import React, { useCallback, useEffect, useState } from 'react';
import { getCourse2User } from 'src/api/apis';
import useCurrentUser from '../../hooks/useCurrentUser';
import { UID_KEY } from '../../hooks/useLogin';
import useUserCourseHistory from '../../hooks/useUserCourseHistory';
import { type Course, type CourseHistory, type User } from '../../utils/type';
import { courseListSx, courseCardSx, courseDialogSx } from './styles';

const QUIZ_IDS = ['A4Q', 'C2Q', 'C3Q', 'C4Q'];
interface Props {
  courses: Course[];
}

function hasPassCorrespondingCourses (courseid: string, courses: Course[], histories: CourseHistory[]) {
  let isPass = false;
  let targetCourseIds: string[] = [];
  switch (courseid) {
    case 'A4Q':
      targetCourseIds = ['A3E', 'C1E'];
      break;
    case 'C2Q':
      targetCourseIds = ['C2C', 'C2E'];
      break;
    case 'C3Q':
      targetCourseIds = ['C3C', 'C3E'];
      break;
    case 'C4Q':
      targetCourseIds = ['C4C', 'C4E'];
      break;
    default:
      return true;
  }

  const chineseCourse = courses.find(c => c.courseid === targetCourseIds[0]);
  const englishCourse = courses.find(c => c.courseid === targetCourseIds[1]);
  const chinesePassScore = Number(chineseCourse?.score_pass ?? 0);
  const englishPassScore = Number(englishCourse?.score_pass ?? 0);
  histories.forEach(history => {
    const historyScore = Number(history.score);
    if (history.courseid === targetCourseIds[0] && historyScore >= chinesePassScore) {
      isPass = true;
    } else if (history.courseid === targetCourseIds[1] && historyScore >= englishPassScore) {
      isPass = true;
    }
  });

  return isPass;
}

export default function CourseList (props: Props) {
  const { courses } = props;
  const [activeCourseUrl, setActiveUrl] = useState<string | null>(null);
  const [showDialog, toggleDialog] = useState(false);
  const { histories, refetchHistories } = useUserCourseHistory();
  const user = useCurrentUser();

  const onOpenCourse = async (course: Course) => {
    // This is a quiz & user didn't pass corresponding course yet
    if (QUIZ_IDS.includes(course.courseid) && !hasPassCorrespondingCourses(course.courseid, courses, histories)) {
      toggleDialog(true);
      return;
    }
    const uid = sessionStorage.getItem(UID_KEY);
    if (!uid) {
      return;
    }
    const url = `${course.test_url}?uid=${uid}&courseid=${course.courseid}`;
    // Notify backend that user is opening this course
    const response = await getCourse2User({
      starttime: (new Date()).toISOString(),
      courseid: course.courseid,
      uid,
    });

    console.log(response);
    setActiveUrl(url);
  };

  const onReceiveMessage = useCallback((event: MessageEvent<any>) => {
    console.log('Get event from iframe', event);
    // Do you things

    // When you want to close iframe: setActiveUrl(null)
  }, []);

  useEffect(() => {
    // Currently no active course
    if (!activeCourseUrl) {
      return;
    }

    window.addEventListener('message', onReceiveMessage);

    return () => { window.removeEventListener('message', onReceiveMessage); };
  }, [activeCourseUrl, onReceiveMessage]);

  return (
    <Box component="section" sx={courseListSx}>
      {courses.map((course, idx) => {
        const isQuiz = QUIZ_IDS.includes(course.courseid);
        const isFinished =
          histories.findIndex(h => {
            if (isQuiz) {
              return h.courseid === course.courseid && Number(h.score) >= Number(course.score_pass);
            } else {
              return h.courseid === course.courseid;
            }
          }) > -1;
        return <CourseCard user={user} key={idx} course={course} onOpen={onOpenCourse} isFinished={isFinished} />;
      })}
      {activeCourseUrl && (
        <div className="iframe-wrapper">
          <div className="iframe-controller">
            <i className="bi bi-arrows-fullscreen"
              onClick={() => {
                const iframe = document.querySelector('iframe');
                if (iframe) {
                  iframe.requestFullscreen();
                }
              }}
            />
            <i
              className="bi bi-x"
              onClick={() => {
                setActiveUrl(null);
                refetchHistories();
              }}
            />
          </div>
          <iframe src={activeCourseUrl} title="course" />
        </div>
      )}
      {showDialog && <CourseDialog onClose={() => { toggleDialog(false); }} />}
    </Box>
  );
}

const CourseCard: React.FC<{ user: User; course: Course; isFinished: boolean; onOpen: (course: Course) => Promise<void>; }> = ({
  user,
  course,
  isFinished,
  onOpen,
}) => {
  let isMandatory = false;

  if (user) {
    const departments = user.department.split('!');
    course.mandatory.forEach(m => {
      if (departments.includes(m)) {
        isMandatory = true;
      }
    });
  }
  return (
    <Box component="figure" sx={courseCardSx} onClick={() => { onOpen(course); }}>
      <img className="thumbnail" src={course.thumbnail} alt="img" />

      {isFinished && <div className="completed">Completed</div>}
      <div className="wrapper">
        <div className="title">
          {course.category1} {isMandatory && <span className="mandatory">Mandatory</span>}
        </div>
        <div className="info">
          <span>Time: {(Number(course.duration) / 60).toFixed(0)} mins</span>
        </div>
        <div className="content">{course.description}</div>
      </div>
    </Box>
  );
};

const CourseDialog: React.FC<{ onClose: () => void; }> = ({ onClose }) => {
  return (
    <Dialog open={true} sx={courseDialogSx}>
      <DialogTitle className="title">Reminder</DialogTitle> <i className="bi bi-x" onClick={onClose} />
      <DialogContent>
        <div className="content">Please complete the course before start the quiz.</div>
        <div className="content">請在開始測驗之前完成單元課程。</div>
        <Button className="button" onClick={onClose}>
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
};
